import {
    useCallback,
    useEffect,
    useState,
    type FC
} from "react";
import {
    type ViewStyle,
    Pressable,
    Platform,
    Image,
    View
} from "react-native";
import type ISiteLogoProps from "./type";
import type {
    SiteLogoKeyboardEvent
} from "./type";
import stylesheet, {
    getSiteLogoIsStretched,
    getSiteLogoMarkSize,
    getSiteLogoSizeKey,
    getSiteLogoColors,
    getSiteLogoSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    Home as HomeIcon
} from "lucide-react-native";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Loading from "../loading";
import Text from "../text";

const SiteLogo: FC<ISiteLogoProps> = ({
    logoBackgroundColor = "default",
    isWorkWithAction = false,
    "aria-label": ariaLabel,
    isWorkWithFlex1 = true,
    isShowBorder = false,
    subTitleCustomColor,
    accessibilityLabel,
    isDisabled = false,
    isCustomImageSize,
    isLoading = false,
    isShowIcon = true,
    image: ImageProp,
    titleCustomColor,
    backgroundColor,
    subTitleVariant,
    customLocalize,
    icon: IconProp,
    size = "large",
    subTitleColor,
    subTitleStyle,
    titleVariant,
    borderColor,
    customTheme,
    imageSource,
    subTitleKey,
    imageProps,
    imageSpace,
    titleColor,
    titleStyle,
    iconColor,
    imageUrl,
    subTitle,
    titleKey,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    role,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        erroredImageUrl,
        setErroredImageUrl
    ] = useState<string | undefined>(undefined);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onFocus,
        onBlur
    });

    const isActionable = isWorkWithAction;

    const currentTitle = titleKey ? localize(titleKey) : title;

    const currentSubTitle = subTitleKey ? localize(subTitleKey) : subTitle;

    const isTitleVisible = !!currentTitle;

    const currentSize = getSiteLogoSize({
        controlHeights,
        iconSizes,
        spaces,
        size
    });

    const currentMarkSize = getSiteLogoMarkSize({
        isCustomImageSize,
        isActionable,
        currentSize,
        imageSpace,
        radiuses,
        spaces
    });

    const currentColors = getSiteLogoColors({
        logoBackgroundColor,
        subTitleCustomColor,
        titleCustomColor,
        backgroundColor,
        borderColor,
        isDisabled,
        iconColor,
        colors
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const isStretched = getSiteLogoIsStretched({
        surfaceStyle
    });

    const isFlexRoot = isActionable && isWorkWithFlex1 && !isStretched;

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        image: imageDynamicStyle,
        mark: markDynamicStyle,
        root: rootDynamicStyle
    } = useStyles({
        currentMarkSize,
        isWorkWithFlex1,
        isFocusVisible,
        isTitleVisible,
        currentColors,
        isActionable,
        isShowBorder,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        isFlexRoot,
        isLoading,
        radiuses,
        borders,
        colors,
        spaces
    });

    const currentLabel = ariaLabel ?? accessibilityLabel ?? (isActionable && currentTitle ? localize("site-logo-home", [
        currentTitle
    ]) : undefined);

    useEffect(() => {
        if(size === "small" || size === "medium" || size === "large") {
            return;
        }

        warnOnce({
            message: `SiteLogo: size "${size}" is deprecated and will be removed in the next major version. Use "${getSiteLogoSizeKey({
                size
            })}" instead.`
        });
    }, [
        size
    ]);

    useEffect(() => {
        if(!isActionable || currentLabel) {
            return;
        }

        warnOnce({
            message: "SiteLogo: an actionable logo without a title needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce the link."
        });
    }, [
        isActionable,
        currentLabel
    ]);

    const onImageError = useCallback(() => {
        setErroredImageUrl(imageUrl);
    }, [
        imageUrl
    ]);

    const onLinkKeyDown = (event: SiteLogoKeyboardEvent) => {
        if(event.nativeEvent.key !== "Enter" || event.nativeEvent.repeat || isDisabled || isLoading || !onPress) {
            return;
        }

        onPress();
    };

    const renderLoading = () => {
        let loadingColor: keyof NCoreUIKit.IconContentColors = "default";

        if(!currentTitle && (imageUrl || ImageProp)) {
            loadingColor = "mid";
        }

        return <Loading
            customColor={isDisabled ? currentColors.iconColor : undefined}
            size={currentSize.iconSize}
            customTheme={customTheme}
            color={loadingColor}
            aria-hidden={true}
        />;
    };

    const renderIcon = () => {
        if(IconProp) {
            return <IconProp
                color={isDisabled ? "disabled" : "default"}
                customColor={currentColors.iconColor}
                size={currentSize.iconSize}
            />;
        }

        return <HomeIcon
            color={currentColors.iconColor}
            size={currentSize.iconSize}
        />;
    };

    const renderMarkContent = () => {
        if(isLoading) {
            return renderLoading();
        }

        if(IconProp || (!ImageProp && !imageUrl) || (!!imageUrl && erroredImageUrl === imageUrl)) {
            return renderIcon();
        }

        if(ImageProp) {
            return <ImageProp
                {...imageProps}
                height={currentMarkSize.imageHeight}
                width={currentMarkSize.imageWidth}
                onError={onImageError}
                style={[
                    imageProps?.style,
                    stylesheet.image,
                    imageDynamicStyle
                ]}
            />;
        }

        return <Image
            {...imageProps}
            height={currentMarkSize.imageHeight}
            width={currentMarkSize.imageWidth}
            onError={onImageError}
            style={[
                imageProps?.style,
                stylesheet.image,
                imageDynamicStyle
            ]}
            source={{
                uri: imageUrl,
                ...imageSource
            }}
        />;
    };

    const renderMark = () => {
        if(!isShowIcon) {
            return null;
        }

        return <View
            style={[
                stylesheet.mark,
                markDynamicStyle
            ]}
            aria-hidden={true}
        >
            {renderMarkContent()}
        </View>;
    };

    const renderSubTitle = () => {
        if(!currentSubTitle) {
            return null;
        }

        return <Text
            variant={subTitleVariant ? subTitleVariant : currentSize.subTitleSize}
            color={subTitleColor ? subTitleColor : "mid"}
            customColor={currentColors.subTitleColor}
            customTheme={customTheme}
            style={subTitleStyle}
            ellipsizeMode="tail"
            numberOfLines={1}
        >
            {currentSubTitle}
        </Text>;
    };

    const renderTitles = () => {
        if(!currentTitle) {
            return null;
        }

        return <View
            style={stylesheet.titles}
        >
            <Text
                variant={titleVariant ? titleVariant : currentSize.titleSize}
                color={titleColor ? titleColor : "high"}
                customColor={currentColors.titleColor}
                customTheme={customTheme}
                ellipsizeMode="tail"
                style={titleStyle}
                numberOfLines={1}
            >
                {currentTitle}
            </Text>
            {renderSubTitle()}
        </View>;
    };

    const renderContent = () => {
        return <View
            style={[
                stylesheet.content,
                contentDynamicStyle
            ]}
        >
            {renderMark()}
            {renderTitles()}
        </View>;
    };

    if(!isActionable) {
        return <View
            {...props}
            role={role ?? (!currentTitle && currentLabel ? "img" : undefined)}
            accessibilityLabel={currentLabel}
            aria-disabled={isDisabled}
            aria-label={currentLabel}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            aria-busy={isLoading}
            onFocus={onFocus}
            onBlur={onBlur}
        >
            {renderContent()}
        </View>;
    }

    const renderLink = (linkStyle?: ViewStyle) => {
        return <Pressable
            {...props}
            {...interactionHandlers}
            {...(Platform.OS === "web" && (role ?? "link") === "link" ? {
                onKeyDown: onLinkKeyDown
            } : {})}
            onPress={isLoading ? undefined : onPress}
            accessibilityLabel={currentLabel}
            aria-disabled={isDisabled}
            aria-label={currentLabel}
            style={[
                linkStyle,
                stylesheet.container,
                containerDynamicStyle
            ]}
            aria-busy={isLoading}
            disabled={isDisabled}
            role={role ?? "link"}
        >
            <View
                style={[
                    surfaceStyle,
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    isHovered={!isLoading && isHovered}
                    isPressed={!isLoading && isPressed}
                    borderRadius={radiuses.actions}
                    customTheme={customTheme}
                />
                {renderContent()}
            </View>
        </Pressable>;
    };

    if(!isFlexRoot) {
        return renderLink(containerStyle);
    }

    return <View
        style={[
            containerStyle,
            stylesheet.root,
            rootDynamicStyle
        ]}
    >
        {renderLink()}
    </View>;
};
export default SiteLogo;
